/**
 * @file TeamJoinPage.jsx
 * @description Where a team invite email lands: `abacases.com/team-join?token=<token>`.
 *   The invited teammate creates their own login for the agency. The token is checked
 *   by the server twice: team_invite_lookup on load (what to show) and handle_new_user
 *   at signup (the real check). Logic lives in lib/agencyContext.js; this is the screen.
 *
 *   Mounted by App.js off the pathname, ahead of every gate (same as UnsubscribePage),
 *   because it must work logged out, straight from an inbox, on a phone.
 * @module components/TeamJoinPage
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { Btn, Input } from "./UI";
import Logo from "./Logo";
import { UsersIcon, AlertTriangleIcon } from "./Icons";
import { usePageSeo } from "../lib/seo";
import { lookupTeamInvite, signUpTeamMember, takeTeamJoinToken, clearTeamJoinToken } from "../lib/agencyContext";

export default function TeamJoinPage({ session, onSignOut }) {
  const { t } = useTranslation();
  usePageSeo({ title: "Join your team | ABAcases", noindex: true });

  const [token] = useState(() => takeTeamJoinToken());
  const [invite, setInvite] = useState(null); // { status, agencyName, email, hasAccount }
  const [name, setName] = useState("");
  const [pw, setPw] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setInvite({ status: "invalid" }); return; }
    let live = true;
    lookupTeamInvite(token).then((res) => { if (live) setInvite(res); });
    return () => { live = false; };
  }, [token]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { setError(t("team.join.errName")); return; }
    if (pw.length < 8) { setError(t("auth.errorPasswordTooShort")); return; }
    if (!agreed) { setError(t("auth.errorLegalAcceptance")); return; }
    setError("");
    setSubmitting(true);
    const res = await signUpTeamMember({ token, email: invite.email, password: pw, fullName: name });
    if (!res.ok) {
      setSubmitting(false);
      setError(res.code === "invalid" ? t("team.join.invalidBody") : res.message);
      return;
    }
    clearTeamJoinToken();
    // A fresh boot picks up the new session and lands on the agency dashboard.
    window.location.assign("/dashboard");
  }, [name, pw, agreed, token, invite, t]);

  const agency = invite?.agencyName || t("team.join.yourAgency");
  let state; // { tone, title, body, form?, signOut? }
  if (!invite) {
    state = { tone: "info", title: t("team.join.loading") };
  } else if (session) {
    state = { tone: "warn", title: t("team.join.signedInTitle"), body: t("team.join.signedInBody", { email: session.user?.email || "", agency }), signOut: true };
  } else if (invite.status === "valid" && invite.hasAccount) {
    state = { tone: "warn", title: t("team.join.hasAccountTitle"), body: t("team.join.hasAccountBody", { email: invite.email, agency }) };
  } else if (invite.status === "valid") {
    state = { tone: "info", title: t("team.join.title", { agency }), body: t("team.join.body", { agency }), form: true };
  } else if (invite.status === "expired") {
    state = { tone: "warn", title: t("team.join.expiredTitle"), body: t("team.join.expiredBody", { agency }) };
  } else if (invite.status === "used") {
    state = { tone: "info", title: t("team.join.usedTitle"), body: t("team.join.usedBody") };
  } else {
    state = { tone: "warn", title: t("team.join.invalidTitle"), body: t("team.join.invalidBody") };
  }

  const accent = state.tone === "warn" ? T.red : T.blue;
  const Icon = state.tone === "warn" ? AlertTriangleIcon : UsersIcon;

  return (
    <div style={{
      minHeight: "100vh", background: T.bg, fontFamily: T.fontBody, color: T.text,
      padding: "32px 16px calc(48px + env(safe-area-inset-bottom, 0px))",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <div style={{ marginBottom: 20 }}>
        <Logo variant="lockup" height={30} alt="ABAcases" />
      </div>

      <div style={{
        width: "100%", maxWidth: 460, background: T.surface,
        border: `1px solid ${T.border}`, borderRadius: T.radiusLg,
        boxShadow: T.shadow, padding: "26px 22px 24px", boxSizing: "border-box",
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: T.radiusFull, background: `${accent}14`,
          display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
        }}>
          <Icon size={20} color={accent} />
        </div>

        <h1 style={{ margin: 0, fontSize: 20, lineHeight: 1.3, fontWeight: 800, letterSpacing: "-0.02em", color: T.text }}>
          {state.title}
        </h1>
        {state.body && (
          <p style={{ margin: "12px 0 0", fontSize: 14.5, lineHeight: 1.6, color: T.textMid }}>{state.body}</p>
        )}

        {state.form && (
          <form onSubmit={submit} style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 14 }}>
            <Input label={t("team.join.email")} value={invite.email || ""} onChange={() => {}} disabled />
            <Input label={t("team.join.name")} value={name} onChange={(e) => setName(e.target.value)} placeholder={t("team.join.namePlaceholder")} />
            <Input label={t("team.join.password")} type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("team.join.passwordPlaceholder")} />
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5, color: T.textMid, cursor: "pointer" }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                {t("team.join.agreePrefix")}{" "}
                <a href="/terms-of-use" target="_blank" rel="noreferrer" style={{ color: T.blue }}>{t("team.join.terms")}</a>{" "}
                {t("team.join.and")}{" "}
                <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ color: T.blue }}>{t("team.join.privacy")}</a>.
              </span>
            </label>
            {error && <div role="alert" style={{ fontSize: 13, color: T.red, lineHeight: 1.5 }}>{error}</div>}
            <Btn type="submit" disabled={submitting} style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}>
              {submitting ? t("team.join.creating") : t("team.join.create")}
            </Btn>
          </form>
        )}

        {state.signOut && (
          <div style={{ marginTop: 20 }}>
            <Btn variant="outline" onClick={onSignOut} style={{ width: "100%", padding: "13px 20px", fontSize: 15.5, borderRadius: T.radiusMd }}>
              {t("team.join.signOut")}
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}
